/**
 * SOUND-643 Phase 1 — turn `applicationSchema` + an application document into a
 * flat render model for the CRM record view.
 *
 * WHY A MODEL RATHER THAN RENDERING DIRECTLY
 * ------------------------------------------
 * sound-app has no component-test harness, so anything expressed inside JSX is
 * unverifiable. Everything that can be decided — which fields exist, which are
 * visible, which group they belong to, what value they hold, whether that value
 * is empty — is decided here, where it has tests. The component that consumes
 * this does nothing but map over the result.
 *
 * WHAT IT FIXES
 * -------------
 * The read view was a hand-written list of <InfoField> calls that had drifted to
 * 21 of 55 scalar fields. Whole groups were invisible, including `riskAssessment`
 * — the merchant's own disclosures about terminations, PCI compliance and Visa
 * risk programs. Deriving the model from the schema means a field cannot exist in
 * the data and be absent from the page.
 *
 * DESIGN RULES (CPO, 2026-08-13)
 * ------------------------------
 * - EVERY field renders. No summary flag, no "show all N", no consolidation.
 * - EMPTY fields render too. A blank row is how you discover the field exists —
 *   `effectiveDate` sat null on 261 MIDs precisely because nothing drew it.
 * - Only the groupings ALREADY in the schema are used. Steps without groups
 *   render as one unnamed group under the step's own title.
 * - Repeatable steps (`owners`, `dbas`) render INLINE as field grids, one block
 *   per item — not promoted to a related-record rail.
 */

import { getVisibleFields } from "./schemaEval";
import { EMPTY_DISPLAY, isEmptyValue, displayValue } from "./displayValue";

// Emptiness + display formatting live in utilities/displayValue so the shared
// <InfoField> uses the SAME definition — they previously disagreed, one showing
// an em-dash and the other "Not Provided", inches apart on this very tab.
//
// IMPORTED and re-exported, deliberately NOT `export { x } from "..."`. A bare
// re-export forwards the binding to importers without introducing it into this
// module's scope, so buildFields() below would throw ReferenceError on
// isEmptyValue. That exact mistake broke every application read earlier today.
export { EMPTY_DISPLAY, isEmptyValue, displayValue };

/**
 * Build the field descriptors for one data object against a list of schema fields.
 * `itemData` is the section object (or one repeatable item); `fullData` is the
 * whole application, which `visibleWhen` predicates may reference.
 */
function buildFields(fields, itemData, fullData) {
  return getVisibleFields(fields, itemData, fullData).map((f) => {
    const value = itemData ? itemData[f.name] : undefined;
    const empty = isEmptyValue(value);
    return {
      name: f.name,
      label: f.label || f.name,
      type: f.type || "text",
      // `gridCols` is the COLUMN COUNT the field wants (1 = full width,
      // 2 = halves, 3 = thirds) — NOT a span. Passed through so the renderer
      // can honour it without re-deriving the meaning.
      gridCols: f.gridCols || 2,
      tooltip: f.tooltip || null,
      required: Boolean(f.required),
      options: f.options || null,
      // `select-other` pairs a choice with a free-text companion field that
      // lives in the same section. The editor needs BOTH the companion's name
      // and its current value, or picking "Other" has nowhere to write.
      otherField: f.otherField || null,
      otherValue: f.otherField && itemData ? itemData[f.otherField] : undefined,
      // Carried through for validation in the inline editor (SOUND-777):
      // percentBounds identifies a percentage by its schema GROUP, and honours
      // any bounds the schema declares.
      group: f.group || null,
      unit: f.unit || null,
      min: f.min ?? null,
      max: f.max ?? null,
      value,
      display: displayValue(value),
      isEmpty: empty,
    };
  });
}

/**
 * Bucket fields into the step's declared groups, or a single unnamed group when
 * the step declares none. Normalising both cases to the same shape means the
 * renderer never branches on "does this step have groups".
 *
 * A declared group with no visible fields is dropped — an empty titled container
 * is noise, and `visibleWhen` can legitimately empty one out.
 */
function buildGroups(step, itemData, fullData) {
  const fields = buildFields(step.fields || [], itemData, fullData);

  if (!step.groups || step.groups.length === 0) {
    return fields.length ? [{ key: null, title: null, variant: null, fields }] : [];
  }

  const byKey = new Map(step.groups.map((g) => [g.key, []]));
  const ungrouped = [];
  for (const f of fields) {
    const def = (step.fields || []).find((x) => x.name === f.name);
    const key = def && def.group;
    if (key && byKey.has(key)) byKey.get(key).push(f);
    else ungrouped.push(f);
  }

  const groups = step.groups
    .map((g) => ({
      key: g.key,
      title: g.title || null,
      variant: g.variant || null,
      fields: byKey.get(g.key) || [],
    }))
    .filter((g) => g.fields.length > 0);

  // A field whose `group` matches nothing declared would otherwise vanish.
  if (ungrouped.length) {
    groups.push({ key: null, title: null, variant: null, fields: ungrouped });
  }
  return groups;
}

/**
 * Build the full render model.
 *
 * @param {object} schema       applicationSchema (from GET /application-schema)
 * @param {object} application  the stored application document
 * @returns {Array} sections — each with `blocks`, each block with `groups`
 */
export function buildRecordSections(schema, application) {
  if (!schema || !Array.isArray(schema.steps)) return [];
  const data = application || {};

  return schema.steps.map((step) => {
    const base = {
      key: step.key,
      title: step.title || step.key,
      description: step.description || null,
      repeatable: step.sectionType === "repeatable",
      itemLabel: step.itemLabel || null,
    };

    if (base.repeatable) {
      const items = Array.isArray(data[step.arrayField]) ? data[step.arrayField] : [];
      return {
        ...base,
        arrayField: step.arrayField,
        count: items.length,
        blocks: items.map((item, i) => ({
          // Prefer the item's own name so a reader can tell which owner or DBA
          // they are looking at without counting rows.
          label:
            item.fullName ||
            item.dbaName ||
            `${step.itemLabel || "Item"} ${i + 1}`,
          index: i,
          // The row's own identifier, so a per-row save can address it. Without
          // this the only handle is the array index, which shifts the moment
          // anyone adds or removes a row — and an edit would land on a
          // different person.
          rowId: item.ownerId || item.dbaId || null,
          groups: buildGroups(step, item, data),
        })),
      };
    }

    return {
      ...base,
      count: null,
      blocks: [{ label: null, index: 0, groups: buildGroups(step, data[step.key], data) }],
    };
  });
}

/**
 * How many fields the model will draw, and how many are blank. Used for the
 * per-section counter — the blank count is the point, since it is the visible
 * signal of what the record is missing.
 */
export function countSectionFields(section) {
  let total = 0;
  let empty = 0;
  for (const block of section.blocks || []) {
    for (const group of block.groups || []) {
      for (const f of group.fields) {
        total += 1;
        if (f.isEmpty) empty += 1;
      }
    }
  }
  return { total, empty, filled: total - empty };
}
